/**
 * useVideoUpload Hook
 * Orchestrates 6-stage video upload with progress tracking
 * Web-specific: No compression stage (desktop users have bandwidth)
 */

import { useState, useCallback } from 'react';
import * as prospectMediaService from '../services/prospect-media';
import { generateThumbnail, getVideoDuration } from '../utils/video-thumbnail';
import { getFileExtension } from '../utils/video-validation';
import { retryOperation } from '../utils/retry';
import type { VideoUploadProgress, VideoUploadResult } from '../types/prospect-media';

export function useVideoUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<VideoUploadProgress>({
    stage: 'generating_thumbnail',
    percent: 0,
    message: 'Preparing upload...',
  });

  const uploadVideo = useCallback(async (
    prospectId: string,
    videoFile: File
  ): Promise<VideoUploadResult> => {
    const startTime = Date.now();
    setIsUploading(true);

    try {
      // Stage 1: Generate thumbnail (0-15%)
      setProgress({
        stage: 'generating_thumbnail',
        percent: 0,
        message: 'Generating thumbnail...',
      });

      const { blob: thumbnailBlob } = await retryOperation(() => generateThumbnail(videoFile));

      setProgress({
        stage: 'generating_thumbnail',
        percent: 15,
        message: 'Getting video metadata...',
      });

      // Stage 2: Get metadata (15-25%)
      const duration = await retryOperation(() => getVideoDuration(videoFile));
      const extension = getFileExtension(videoFile);
      const contentType = videoFile.type;

      setProgress({
        stage: 'generating_thumbnail',
        percent: 25,
        message: 'Requesting upload URLs...',
      });

      // Stage 3: Request upload URLs (25-30%)
      const uploadUrls = await retryOperation(() =>
        prospectMediaService.requestVideoUploadUrl(prospectId, {
          fileExtension: extension,
          contentType,
          fileSizeBytes: videoFile.size,
          durationSeconds: duration,
        })
      );

      // Stage 4: Upload video to GCS (30-75%)
      setProgress({
        stage: 'uploading_video',
        percent: 30,
        message: 'Uploading video...',
      });

      const videoUploadResult = await retryOperation(() =>
        prospectMediaService.uploadVideoToGCS(
          uploadUrls.uploadUrl,
          videoFile,
          contentType,
          (uploadPercent) => {
            // Map upload progress (0-100%) to our progress range (30-75%)
            const overallPercent = 30 + Math.round(uploadPercent * 0.45);
            setProgress({
              stage: 'uploading_video',
              percent: overallPercent,
              message: 'Uploading video...',
            });
          }
        )
      );

      if (!videoUploadResult.success) {
        throw new Error(videoUploadResult.error || 'Video upload failed');
      }

      // Stage 5: Upload thumbnail (75-100%)
      setProgress({
        stage: 'uploading_thumbnail',
        percent: 75,
        message: 'Uploading thumbnail...',
      });

      const thumbnailUploadResult = await retryOperation(() =>
        prospectMediaService.uploadThumbnailToGCS(
          uploadUrls.thumbnailUploadUrl,
          thumbnailBlob
        )
      );

      if (!thumbnailUploadResult.success) {
        throw new Error(thumbnailUploadResult.error || 'Thumbnail upload failed');
      }

      // Stage 6: Complete (100%)
      setProgress({
        stage: 'complete',
        percent: 100,
        message: 'Upload complete!',
      });

      setIsUploading(false);

      return {
        success: true,
        mediaId: uploadUrls.mediaId,
        gcsUrl: uploadUrls.gcsUrl,
        thumbnailGcsUrl: uploadUrls.thumbnailGcsUrl,
        uploadTimeMs: Date.now() - startTime,
      };
    } catch (error) {
      console.error('[VideoUpload] Upload failed', {
        error,
        prospectId,
        fileName: videoFile.name,
        fileSize: videoFile.size,
        currentStage: progress.stage,
        currentPercent: progress.percent,
        uploadTimeMs: Date.now() - startTime,
      });

      setIsUploading(false);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error),
        uploadTimeMs: Date.now() - startTime,
      };
    }
  }, []);

  return {
    uploadVideo,
    isUploading,
    progress,
  };
}
